import type {FC} from 'react';
import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import type {Checkin, Journal} from 'api/v2';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Stack,
} from '@wellstone-solutions/web';
import {Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Theme} from '@wellstone-solutions/common';
import {checkinsQueryOptions} from 'queries';
import {useTextClamp, getClampStyles} from 'hooks/useTextClamp';

interface CheckinsCardProps {
  checkin: Checkin;
  journal?: Journal | null;
}

const DEFAULT_CHIP_LIMIT = 3;

export const CheckinsCard: FC<CheckinsCardProps> = ({checkin, journal}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Checkin options
  const optionsQuery = useQuery(checkinsQueryOptions.optionsList());
  const options = optionsQuery.data?.results ?? [];

  // Journal content - now passed as prop instead of individual query
  const hasJournal = !!journal;
  const {ref: journalRef, isClamped: isJournalClamped} = useTextClamp();

  // Header content
  const createdDate = new Date(checkin.created);
  const date = createdDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  });

  const time = createdDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  const headerTitle = `${date} ${time}`;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Chips
  const hasMoreChips = checkin.states.length > DEFAULT_CHIP_LIMIT;
  const visibleChips =
    hasMoreChips && !isExpanded
      ? checkin.states.slice(0, DEFAULT_CHIP_LIMIT)
      : checkin.states;

  const renderChip = (state: string) => {
    const match = options.find((opt) => opt.value === state);
    const label = match?.title ?? state;
    const backgroundColor = match?.color;
    const fontColor =
      backgroundColor === Theme.colorPalette.medium
        ? Theme.colorPalette.darkest
        : Theme.colorPalette.lightest;

    return (
      <Chip
        key={state}
        label={label}
        variant="filled"
        sx={{
          backgroundColor: backgroundColor,
          color: fontColor,
          fontWeight: 700,
          marginRight: 1,
          marginBottom: 1,
        }}
      />
    );
  };

  // Levels and custom word
  const {stressLevel, energyLevel, customWord} = checkin;
  const hasLevels = stressLevel != null || energyLevel != null;

  const renderDetail = (label: string, value: string | number) => (
    <Typography>
      <Box component="span" sx={{fontWeight: 700}}>
        {label}:
      </Box>{' '}
      {value}
    </Typography>
  );

  const showActions = hasMoreChips || isJournalClamped || isExpanded;

  return (
    <Card sx={{mb: 2, elevation: 1}}>
      <CardHeader
        title={headerTitle}
        titleTypographyProps={{variant: 'caption', fontWeight: 400}}
        sx={{
          pt: 1,
          pb: 0.5,
          backgroundColor: Theme.colorPalette.light,
          letterSpacing: '0.4px',
          borderBottom: `1px solid ${Theme.colorPalette.medium}`,
        }}
      />

      <CardContent>
        <Stack direction="row" flexWrap="wrap" justifyContent="flex-start">
          {visibleChips.map((state) => renderChip(state))}

          {hasMoreChips && !isExpanded && (
            <Typography
              variant="caption"
              sx={{ml: 0.5, fontWeight: 400}}
              alignContent="center">
              +{checkin.states.length - DEFAULT_CHIP_LIMIT} more
            </Typography>
          )}
        </Stack>

        {hasLevels && (
          <Stack direction="row" spacing={3} sx={{mt: 0.5}}>
            {stressLevel != null && renderDetail('Stress level', stressLevel)}
            {energyLevel != null && renderDetail('Energy level', energyLevel)}
          </Stack>
        )}

        {!!customWord && renderDetail('My word today is', customWord)}

        {hasJournal && (
          <Box sx={{mt: 1, maxHeight: '400px', overflowY: 'auto'}}>
            <Typography
              ref={journalRef}
              sx={!isExpanded ? getClampStyles(1) : {}}>
              {journal?.content}
            </Typography>
          </Box>
        )}
      </CardContent>

      {showActions && (
        <CardActions sx={{justifyContent: 'flex-end'}}>
          <Button
            variant="text"
            disableRipple
            sx={{
              '&:hover': {
                backgroundColor: 'transparent',
              },
              fontSize: 13,
            }}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transition: 'transform 0.3s',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
                fontSize="small"
              />
            }
            onClick={toggleExpanded}>
            {isExpanded ? 'View less' : 'View more'}
          </Button>
        </CardActions>
      )}
    </Card>
  );
};
